import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import type { DeepSkyObject } from '@/constants/deep-sky'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { registerSkyPointer } from '@/lib/sky-pointer'
import { prefersReducedMotion } from '@/lib/preferences'
import { FadeLink } from '@/components/generals/fade-link'
import { RevealLines, Reveal } from '@/components/generals/reveal'
import { DEEP_SKY_OBJECTS } from '@/constants/deep-sky'
import styles from '@/components/landing/eyepiece.module.css'

const STORAGE_KEY = 'aphelion:eyepiece-found'
/** How long the eyepiece has to rest on an object before it is logged as found. */
const ACQUIRE_MS = 650
const BRASS = '198, 167, 110'
const DECODE_GLYPHS = '0123456789ABCDEFGHJKLMNPRSTUVWXYZ+·'

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

type Sprite = {
    object: DeepSkyObject
    sharp: HTMLCanvasElement | null
    soft: HTMLCanvasElement | null
}

const seeded = (seed: number) => {
    let state = seed

    return () => {
        state = (state * 1664525 + 1013904223) >>> 0

        return state / 4294967296
    }
}

const makeCanvas = (size: number): [HTMLCanvasElement, CanvasRenderingContext2D] => {
    const canvas = document.createElement('canvas')

    canvas.width = size
    canvas.height = size

    const context = canvas.getContext('2d') as CanvasRenderingContext2D

    return [canvas, context]
}

const drawStar = (context: CanvasRenderingContext2D, x: number, y: number, radius: number, colour: [number, number, number], glow: number) => {
    const [r, g, b] = colour
    const halo = context.createRadialGradient(x, y, 0, x, y, radius * glow)

    halo.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.9)`)
    halo.addColorStop(0.18, `rgba(${r}, ${g}, ${b}, 0.35)`)
    halo.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
    context.fillStyle = halo
    context.beginPath()
    context.arc(x, y, radius * glow, 0, Math.PI * 2)
    context.fill()
    context.fillStyle = `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 30)}, ${Math.min(255, b + 30)})`
    context.beginPath()
    context.arc(x, y, radius, 0, Math.PI * 2)
    context.fill()
}

/** Star fields we draw ourselves, from the real colours and shapes of each object. */
export const drawProcedural = (kind: NonNullable<DeepSkyObject['procedural']>, size: number): HTMLCanvasElement => {
    const [canvas, context] = makeCanvas(size)
    const random = seeded(kind.length * 977)
    const s = size / 256

    context.fillStyle = '#000'
    context.fillRect(0, 0, size, size)

    for (let i = 0; i < 60; i++) {
        drawStar(context, random() * size, random() * size, (0.35 + random() * 0.5) * s, [220, 225, 240], 3)
    }

    if (kind === 'double-star') {
        drawStar(context, 108 * s, 132 * s, 6.5 * s, [255, 206, 120], 9)
        drawStar(context, 152 * s, 118 * s, 4 * s, [140, 182, 255], 9)
    }

    if (kind === 'pleiades') {
        const stars: [number, number, number][] = [
            [128, 120, 5.2], [96, 128, 4.2], [150, 104, 4], [168, 136, 3.6], [110, 92, 3.4],
            [140, 150, 3.2], [82, 104, 3], [178, 112, 2.6], [120, 160, 2.4]
        ]

        for (let i = 0; i < 5; i++) {
            const x = (90 + random() * 90) * s
            const y = (80 + random() * 90) * s
            const haze = context.createRadialGradient(x, y, 0, x, y, (40 + random() * 30) * s)

            haze.addColorStop(0, 'rgba(110, 150, 255, 0.14)')
            haze.addColorStop(1, 'rgba(110, 150, 255, 0)')
            context.fillStyle = haze
            context.fillRect(0, 0, size, size)
        }

        stars.forEach(([x, y, radius]) => drawStar(context, x * s, y * s, radius * 0.6 * s, [185, 210, 255], 8))
    }

    if (kind === 'double-cluster') {
        const clusters: [number, number][] = [[92, 128], [168, 120]]

        clusters.forEach(([cx, cy]) => {
            for (let i = 0; i < 70; i++) {
                const radius = Math.abs(random() + random() - 1) * 34 * s
                const angle = random() * Math.PI * 2
                const giant = random() < 0.05

                drawStar(
                    context,
                    cx * s + Math.cos(angle) * radius,
                    cy * s + Math.sin(angle) * radius,
                    (0.5 + random() * 1.1) * s,
                    giant ? [255, 170, 110] : [205, 220, 255],
                    5
                )
            }
        })
    }

    return canvas
}

const loadImage = (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const image = new Image()

    image.decoding = 'async'
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
})

const buildSprite = async (object: DeepSkyObject, size: number): Promise<Sprite> => {
    let sharp: HTMLCanvasElement

    if (object.procedural) {
        sharp = drawProcedural(object.procedural, size)
    }
    else if (object.image) {
        const image = await loadImage(object.image)
        const [canvas, context] = makeCanvas(size)
        const side = Math.min(image.naturalWidth, image.naturalHeight)

        context.drawImage(image, (image.naturalWidth - side) / 2, (image.naturalHeight - side) / 2, side, side, 0, 0, size, size)
        sharp = canvas
    }
    else {
        return { object, sharp: null, soft: null }
    }

    const small = Math.max(8, Math.round(size / 12))
    const [tiny, tinyContext] = makeCanvas(small)
    const [soft, softContext] = makeCanvas(size)

    tinyContext.drawImage(sharp, 0, 0, small, small)
    softContext.imageSmoothingQuality = 'high'
    softContext.drawImage(tiny, 0, 0, size, size)

    const feather = (canvas: HTMLCanvasElement) => {
        const context = canvas.getContext('2d')

        if (!context) return

        const gradient = context.createRadialGradient(size / 2, size / 2, size * 0.26, size / 2, size / 2, size * 0.5)

        gradient.addColorStop(0, 'rgba(0, 0, 0, 1)')
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
        context.globalCompositeOperation = 'destination-in'
        context.fillStyle = gradient
        context.fillRect(0, 0, size, size)
        context.globalCompositeOperation = 'source-over'
    }

    feather(sharp)
    feather(soft)

    return { object, sharp, soft }
}

/**
 * The label beside the eyepiece: the catalogue code decodes character by character and the distance
 * counts up to its real value, while the name and the note settle in through CSS.
 */
const ObjectLabel = ({ object }: { object: DeepSkyObject }): ReactNode => {
    const codeRef = useRef<HTMLParagraphElement | null>(null)
    const distanceRef = useRef<HTMLParagraphElement | null>(null)
    const code = `${object.designation} · ${object.type}`

    useEffect(() => {
        const codeElement = codeRef.current
        const distanceElement = distanceRef.current

        if (!codeElement || !distanceElement) return

        if (prefersReducedMotion()) {
            codeElement.textContent = code
            distanceElement.textContent = object.distance
            return
        }

        const match = object.distance.match(/\d[\d,]*(\.\d+)?/)
        const value = match ? Number(match[0].replace(/,/g, '')) : 0
        const decimals = match?.[1] ? match[1].length - 1 : 0
        const grouping = match ? match[0].includes(',') : false
        const started = performance.now()
        const decodeMs = 160 + code.length * 16
        let frame = 0

        const tick = (now: number) => {
            const elapsed = now - started
            let text = ''

            for (let i = 0; i < code.length; i++) {
                const character = code[i]

                text += character === ' ' || elapsed > 160 + i * 16
                    ? character
                    : DECODE_GLYPHS[Math.floor(Math.random() * DECODE_GLYPHS.length)]
            }

            codeElement.textContent = text

            if (match) {
                const shown = (value * easeOut(clamp01((elapsed - 120) / 900))).toLocaleString('en-GB', {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                    useGrouping: grouping
                })

                distanceElement.textContent = object.distance.replace(match[0], shown)
            }

            if (elapsed < Math.max(decodeMs, 1050)) {
                frame = requestAnimationFrame(tick)
            }
            else {
                codeElement.textContent = code
                distanceElement.textContent = object.distance
            }
        }

        frame = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frame)
    }, [code, object])

    return (
        <>
            <span className={styles.rule} />
            <p ref={codeRef} className='label-mono mt-3 text-brass'>
                {code}
            </p>
            <p className={cn('mt-2 font-heading text-[1.9rem] font-light leading-tight text-star', styles.name)}>
                {object.name}
            </p>
            <p ref={distanceRef} className='data-mono mt-2 text-star-3'>
                {object.distance}
            </p>
            <p className={cn('mt-3 text-[0.95rem] leading-relaxed text-star-2', styles.note)}>
                {object.sentence}
            </p>
        </>
    )
}

type Meteor = {
    x: number
    y: number
    dx: number
    dy: number
    speed: number
    length: number
    life: number
    born: number
}

type Burst = {
    x: number
    y: number
    cx: number
    cy: number
    size: number
    born: number
}

type Twinkler = {
    x: number
    y: number
    r: number
    phase: number
    rate: number
}

/** Shortest signed difference between two angles, in radians. */
const angleDelta = (from: number, to: number): number => {
    let delta = (to - from) % (Math.PI * 2)

    if (delta > Math.PI) delta -= Math.PI * 2
    if (delta < -Math.PI) delta += Math.PI * 2

    return delta
}

/**
 * A wide, almost empty patch of night. Wherever the eyepiece goes, hidden objects swim into focus:
 * twelve of them, real photographs from Hubble and ESO. The field is alive around it: stars twinkle,
 * the odd meteor crosses, faint pulses hint where something is hiding, the focus ring turns as the
 * eyepiece moves, and every object you find locks on, flares and joins your own constellation.
 */
export const Eyepiece = (): ReactNode => {
    const fieldRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const labelRef = useRef<HTMLDivElement | null>(null)
    const pointer = useRef({ x: 0.5, y: 0.5, active: false })
    const viewing = useRef<DeepSkyObject | null>(null)
    const [found, setFound] = useState<string[]>([])
    const [current, setCurrent] = useState<DeepSkyObject | null>(null)
    const [announcement, setAnnouncement] = useState('')
    const [touch, setTouch] = useState(false)
    const foundRef = useRef<string[]>([])

    useEffect(() => {
        setTouch(window.matchMedia('(hover: none), (pointer: coarse)').matches)

        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as unknown

            if (Array.isArray(saved)) {
                const valid = saved.filter((id): id is string => typeof id === 'string' && DEEP_SKY_OBJECTS.some(object => object.id === id))

                foundRef.current = valid
                setFound(valid)
            }
        }
        catch {
            // Nothing saved yet.
        }
    }, [])

    useEffect(() => {
        const field = fieldRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!field || !canvas || !context) return

        let width = 0
        let height = 0
        let ratio = 1
        let background: HTMLCanvasElement | null = null
        let magnified: HTMLCanvasElement | null = null
        let sprites: Sprite[] = []
        let twinklers: Twinkler[] = []
        let frame = 0
        let visible = false
        let ex = 0.5
        let ey = 0.5
        let previousX = -1
        let previousY = -1
        let alpha = 0
        let dwell: { id: string, since: number } | null = null
        let disposed = false
        let lastNow = 0
        let ringAngle = 0
        let glintAngle = -Math.PI * 0.75
        let speed = 0
        let nextMeteor = -1
        let nextPulse = -1
        let pulse: { x: number, y: number, born: number } | null = null
        const meteors: Meteor[] = []
        const bursts: Burst[] = []
        // When each object was found during this visit, so its line in the constellation can draw itself.
        const foundAt = new Map<string, number>()
        const byId = new Map(DEEP_SKY_OBJECTS.map(object => [object.id, object]))
        let mono = 'monospace'

        try {
            const value = getComputedStyle(document.documentElement).getPropertyValue('--font-app-mono').trim()

            if (value) mono = `${value}, monospace`
        }
        catch {
            // The canvas labels fall back to the system monospace face.
        }

        const radius = () => (width < 640 ? Math.min(width * 0.3, 120) : Math.min(Math.max(width * 0.13, 150), 220))
        const objectSize = (object: DeepSkyObject) => object.field.size * Math.max(620, Math.min(width, 1500))

        const paintStars = (count: number, brightness: number, seed: number) => {
            const layer = document.createElement('canvas')

            layer.width = Math.round(width * ratio)
            layer.height = Math.round(height * ratio)

            const layerContext = layer.getContext('2d')

            if (!layerContext) return layer

            const random = seeded(seed)

            layerContext.scale(ratio, ratio)

            for (let i = 0; i < count; i++) {
                const x = random() * width
                const y = random() * height
                const r = 0.3 + Math.pow(random(), 5) * 1.2
                const a = (0.15 + random() * 0.55) * brightness

                layerContext.fillStyle = `rgba(236, 232, 222, ${a})`
                layerContext.beginPath()
                layerContext.arc(x, y, r, 0, Math.PI * 2)
                layerContext.fill()
            }

            return layer
        }

        const resize = () => {
            const rect = field.getBoundingClientRect()

            width = rect.width
            height = rect.height
            ratio = Math.min(window.devicePixelRatio || 1, 2)
            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
            background = paintStars(Math.round(width * height * 0.00009), 0.7, 11)
            magnified = paintStars(Math.round(width * height * 0.0009), 1, 29)

            const random = seeded(53)
            const count = Math.max(18, Math.min(70, Math.round(width * height * 0.00004)))

            twinklers = Array.from({ length: count }, () => ({
                x: random() * width,
                y: random() * height,
                r: 0.6 + Math.pow(random(), 3) * 1.1,
                phase: random() * Math.PI * 2,
                rate: 0.6 + random() * 1.8
            }))
        }

        const objectAt = (x: number, y: number, limit: number): { object: DeepSkyObject, distance: number } | null => {
            let best: { object: DeepSkyObject, distance: number } | null = null

            for (const object of DEEP_SKY_OBJECTS) {
                const distance = Math.hypot(object.field.x * width - x, object.field.y * height - y)

                if (distance < limit && (!best || distance < best.distance)) best = { object, distance }
            }

            return best
        }

        /** Your own constellation: a dotted line joining the objects in the order you found them, each with its code. */
        const drawConstellation = (now: number, still: boolean) => {
            const order = foundRef.current

            if (!order.length) return

            context.save()
            context.strokeStyle = `rgba(${BRASS}, 0.32)`
            context.lineWidth = 1
            context.setLineDash([2, 6])
            context.lineDashOffset = still ? 0 : -now * 0.008

            for (let i = 1; i < order.length; i++) {
                const from = byId.get(order[i - 1])
                const to = byId.get(order[i])

                if (!from || !to) continue

                const at = foundAt.get(to.id)
                const progress = still || at === undefined ? 1 : easeOut(clamp01((now - at - 250) / 1100))

                if (progress <= 0) continue

                const ax = from.field.x * width
                const ay = from.field.y * height
                const bx = to.field.x * width
                const by = to.field.y * height

                context.beginPath()
                context.moveTo(ax, ay)
                context.lineTo(ax + (bx - ax) * progress, ay + (by - ay) * progress)
                context.stroke()
            }

            context.setLineDash([])
            context.font = `400 9px ${mono}`
            context.textBaseline = 'middle'

            for (const id of order) {
                const object = byId.get(id)

                if (!object) continue

                const x = object.field.x * width
                const y = object.field.y * height
                const at = foundAt.get(id)
                const grow = still || at === undefined ? 1 : easeOut(clamp01((now - at) / 600))

                context.globalAlpha = grow
                context.strokeStyle = `rgba(${BRASS}, 0.6)`
                context.beginPath()
                context.arc(x, y, 3 + 3 * grow, 0, Math.PI * 2)
                context.stroke()
                context.fillStyle = `rgba(${BRASS}, 0.9)`
                context.beginPath()
                context.arc(x, y, 1.2, 0, Math.PI * 2)
                context.fill()
                context.fillStyle = `rgba(${BRASS}, 0.55)`
                context.fillText(object.designation.split(' · ')[0].toUpperCase(), x + 10, y)
            }

            context.restore()
        }

        const draw = (now: number) => {
            const still = prefersReducedMotion()
            const dt = lastNow ? Math.min(50, now - lastNow) : 16
            const R = radius()
            const targetX = pointer.current.x * width
            const targetY = pointer.current.y * height

            lastNow = now
            ex += (targetX - ex) * (still ? 1 : 0.2)
            ey += (targetY - ey) * (still ? 1 : 0.2)
            alpha += ((pointer.current.active ? 1 : 0) - alpha) * (still ? 1 : 0.08)

            // How fast the eyepiece is moving turns the focus ring and swings the glint on the brass.
            if (previousX < 0) {
                previousX = ex
                previousY = ey
            }

            const vx = (ex - previousX) / dt
            const vy = (ey - previousY) / dt

            previousX = ex
            previousY = ey
            speed += (Math.hypot(vx, vy) - speed) * 0.12

            if (!still) {
                ringAngle += vx * 0.012 + dt * 0.00005

                if (Math.hypot(vx, vy) > 0.05) glintAngle += angleDelta(glintAngle, Math.atan2(vy, vx)) * 0.12
            }

            context.setTransform(ratio, 0, 0, ratio, 0, 0)
            context.clearRect(0, 0, width, height)

            if (background && background.width > 0 && background.height > 0) context.drawImage(background, 0, 0, width, height)

            // A few brighter stars twinkle across the whole field.
            for (const star of twinklers) {
                const shimmer = still ? 0.6 : 0.5 + 0.5 * Math.sin(now * 0.001 * star.rate + star.phase)
                const light = 0.18 + 0.6 * shimmer

                context.fillStyle = `rgba(240, 236, 226, ${light})`
                context.beginPath()
                context.arc(star.x, star.y, star.r, 0, Math.PI * 2)
                context.fill()

                if (star.r > 1.2 && shimmer > 0.75) {
                    const flare = star.r * 5 * (shimmer - 0.75) * 4

                    context.strokeStyle = `rgba(240, 236, 226, ${0.35 * (shimmer - 0.75) * 4})`
                    context.lineWidth = 0.6
                    context.beginPath()
                    context.moveTo(star.x - flare, star.y)
                    context.lineTo(star.x + flare, star.y)
                    context.moveTo(star.x, star.y - flare)
                    context.lineTo(star.x, star.y + flare)
                    context.stroke()
                }
            }

            drawConstellation(now, still)

            // While nobody is looking, a faint ping now and then gives away where something is hiding.
            if (!still) {
                if (alpha > 0.4) {
                    pulse = null
                    nextPulse = now + 2500
                }
                else if (!pulse && now > nextPulse) {
                    const hidden = DEEP_SKY_OBJECTS.filter(object => !foundRef.current.includes(object.id))
                    const choice = hidden[Math.floor(Math.random() * hidden.length)]

                    if (choice && nextPulse > 0) pulse = { x: choice.field.x * width, y: choice.field.y * height, born: now }

                    nextPulse = now + 3400 + Math.random() * 2200
                }

                if (pulse) {
                    const t = (now - pulse.born) / 2400

                    if (t >= 1) {
                        pulse = null
                    }
                    else {
                        for (const offset of [0, 0.22]) {
                            const local = t - offset

                            if (local <= 0) continue

                            context.strokeStyle = `rgba(${BRASS}, ${0.3 * Math.pow(1 - local, 2)})`
                            context.lineWidth = 1
                            context.beginPath()
                            context.arc(pulse.x, pulse.y, 4 + easeOut(local) * 48, 0, Math.PI * 2)
                            context.stroke()
                        }
                    }
                }

                // The odd meteor streaks across the field.
                if (nextMeteor < 0) nextMeteor = now + 2200 + Math.random() * 3000

                if (now > nextMeteor) {
                    const angle = ((200 + Math.random() * 30) * Math.PI) / 180

                    meteors.push({
                        x: width * (0.35 + Math.random() * 0.7),
                        y: height * (Math.random() * 0.4) - 20,
                        dx: Math.cos(angle),
                        dy: -Math.sin(angle),
                        speed: 0.9 + Math.random() * 0.6,
                        length: 90 + Math.random() * 90,
                        life: 900 + Math.random() * 500,
                        born: now
                    })
                    nextMeteor = now + 4500 + Math.random() * 7000
                }

                for (let i = meteors.length - 1; i >= 0; i--) {
                    const meteor = meteors[i]
                    const age = now - meteor.born

                    if (age > meteor.life) {
                        meteors.splice(i, 1)
                        continue
                    }

                    const envelope = Math.sin((Math.PI * age) / meteor.life)
                    const hx = meteor.x + meteor.dx * meteor.speed * age
                    const hy = meteor.y + meteor.dy * meteor.speed * age
                    const tx = hx - meteor.dx * meteor.length * envelope
                    const ty = hy - meteor.dy * meteor.length * envelope
                    const trail = context.createLinearGradient(tx, ty, hx, hy)

                    trail.addColorStop(0, 'rgba(200, 220, 255, 0)')
                    trail.addColorStop(1, `rgba(255, 248, 232, ${0.85 * envelope})`)
                    context.strokeStyle = trail
                    context.lineWidth = 1.3
                    context.lineCap = 'round'
                    context.beginPath()
                    context.moveTo(tx, ty)
                    context.lineTo(hx, hy)
                    context.stroke()
                }
            }

            const inView = alpha > 0.5 ? objectAt(ex, ey, R * 0.55) : null
            const next = inView?.object ?? null

            if (alpha > 0.01) {
                context.save()
                context.globalAlpha = alpha
                context.beginPath()
                context.arc(ex, ey, R, 0, Math.PI * 2)
                context.clip()
                context.fillStyle = '#020306'
                context.fillRect(ex - R, ey - R, R * 2, R * 2)

                if (magnified && magnified.width > 0 && magnified.height > 0) context.drawImage(magnified, 0, 0, width, height)

                context.globalCompositeOperation = 'lighter'

                for (const sprite of sprites) {
                    if (!sprite.sharp || !sprite.soft) continue

                    const size = objectSize(sprite.object)
                    const ox = sprite.object.field.x * width
                    const oy = sprite.object.field.y * height
                    const distance = Math.hypot(ox - ex, oy - ey)

                    if (distance > R + size * 0.7) continue

                    const focus = 1 - Math.min(1, Math.max(0, (distance - R * 0.12) / (R * 0.85)))
                    const sharpness = focus * focus * (3 - 2 * focus)

                    context.globalAlpha = alpha * (0.45 + 0.55 * focus) * (1 - sharpness)
                    context.drawImage(sprite.soft, ox - size / 2, oy - size / 2, size, size)
                    context.globalAlpha = alpha * (0.45 + 0.55 * focus) * sharpness
                    context.drawImage(sprite.sharp, ox - size / 2, oy - size / 2, size, size)
                }

                context.globalCompositeOperation = 'source-over'
                context.globalAlpha = alpha

                const vignette = context.createRadialGradient(ex, ey, R * 0.5, ex, ey, R)

                vignette.addColorStop(0, 'rgba(0, 0, 0, 0)')
                vignette.addColorStop(0.75, 'rgba(0, 0, 0, 0.25)')
                vignette.addColorStop(1, 'rgba(0, 0, 0, 0.88)')
                context.fillStyle = vignette
                context.fillRect(ex - R, ey - R, R * 2, R * 2)

                // A whisper of colour fringing at the edge of the glass.
                context.lineWidth = 1.2
                context.strokeStyle = 'rgba(110, 170, 255, 0.16)'
                context.beginPath()
                context.arc(ex - 0.8, ey, R - 1.5, 0, Math.PI * 2)
                context.stroke()
                context.strokeStyle = 'rgba(255, 130, 90, 0.14)'
                context.beginPath()
                context.arc(ex + 0.8, ey, R - 1.5, 0, Math.PI * 2)
                context.stroke()

                // The fine field-stop scale engraved just inside the rim, turning the other way.
                context.strokeStyle = 'rgba(216, 189, 133, 0.22)'
                context.lineWidth = 0.8
                context.beginPath()

                for (let degree = 0; degree < 360; degree += 3) {
                    const angle = (degree * Math.PI) / 180 - ringAngle * 0.6
                    const inner = R - (degree % 15 === 0 ? 9 : 5)

                    context.moveTo(ex + Math.cos(angle) * inner, ey + Math.sin(angle) * inner)
                    context.lineTo(ex + Math.cos(angle) * (R - 2), ey + Math.sin(angle) * (R - 2))
                }

                context.stroke()

                // Lock-on: brackets close in on the object while the eyepiece rests on it, then flare when it is logged.
                if (next && inView && inView.distance < R * 0.45) {
                    const isFound = foundRef.current.includes(next.id)
                    const progress = isFound ? 1 : dwell && dwell.id === next.id ? clamp01((now - dwell.since) / ACQUIRE_MS) : 0
                    const box = Math.min(objectSize(next) * 0.42, R * 0.62)
                    const reach = box * (1.55 - 0.55 * easeOut(still ? 1 : progress))
                    const arm = Math.max(8, box * 0.22)
                    const ox = next.field.x * width
                    const oy = next.field.y * height

                    context.strokeStyle = `rgba(${BRASS}, ${0.35 + 0.5 * progress})`
                    context.lineWidth = 1.2
                    context.beginPath()

                    for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
                        const cx = ox + sx * reach
                        const cy = oy + sy * reach

                        context.moveTo(cx - sx * arm, cy)
                        context.lineTo(cx, cy)
                        context.lineTo(cx, cy - sy * arm)
                    }

                    context.stroke()

                    if (!isFound && progress > 0) {
                        context.strokeStyle = `rgba(${BRASS}, 0.85)`
                        context.lineWidth = 2
                        context.beginPath()
                        context.arc(ex, ey, R - 14, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress)
                        context.stroke()
                    }

                    context.font = `400 10px ${mono}`
                    context.textAlign = 'center'
                    context.textBaseline = 'alphabetic'
                    context.fillStyle = `rgba(${BRASS}, ${isFound ? 0.6 : 0.35 + 0.5 * progress})`
                    context.fillText(isFound ? 'IN YOUR LOG' : 'ACQUIRING', ex, ey + R - 26)
                    context.textAlign = 'start'
                }

                // The flash where a new object is logged, and sparks thrown outwards from it.
                for (const burst of bursts) {
                    const t = clamp01((now - burst.born) / 900)

                    if (t >= 1) continue

                    const flash = context.createRadialGradient(burst.x, burst.y, 0, burst.x, burst.y, burst.size * (0.5 + t))

                    flash.addColorStop(0, `rgba(255, 246, 225, ${0.45 * (1 - t)})`)
                    flash.addColorStop(1, 'rgba(255, 246, 225, 0)')
                    context.fillStyle = flash
                    context.fillRect(burst.x - burst.size * 2, burst.y - burst.size * 2, burst.size * 4, burst.size * 4)

                    context.fillStyle = `rgba(${BRASS}, ${1 - t})`

                    for (let i = 0; i < 14; i++) {
                        const angle = (i / 14) * Math.PI * 2 + i
                        const travel = easeOut(t) * R * (0.35 + (i % 3) * 0.12)

                        context.beginPath()
                        context.arc(burst.x + Math.cos(angle) * travel, burst.y + Math.sin(angle) * travel, 1.3, 0, Math.PI * 2)
                        context.fill()
                    }
                }

                context.restore()

                context.save()
                context.globalAlpha = alpha

                const rim = context.createLinearGradient(ex - R, ey - R, ex + R, ey + R)

                rim.addColorStop(0, '#d8bd85')
                rim.addColorStop(0.45, '#6b5634')
                rim.addColorStop(0.7, '#a98c58')
                rim.addColorStop(1, '#4d3d23')
                context.strokeStyle = rim
                context.lineWidth = 9
                context.beginPath()
                context.arc(ex, ey, R + 4.5, 0, Math.PI * 2)
                context.stroke()

                // A glint of light slides round the brass towards the way the eyepiece is moving.
                const glint = Math.min(0.65, 0.12 + speed * 0.9)

                if (!still && glint > 0.13) {
                    context.strokeStyle = `rgba(255, 238, 200, ${glint})`
                    context.lineWidth = 3
                    context.lineCap = 'round'
                    context.beginPath()
                    context.arc(ex, ey, R + 4.5, glintAngle - 0.32, glintAngle + 0.32)
                    context.stroke()
                }

                // The focus ring's graduations turn as the eyepiece moves.
                context.strokeStyle = 'rgba(216, 189, 133, 0.55)'
                context.lineWidth = 1
                context.beginPath()

                for (let degree = 0; degree < 360; degree += 6) {
                    const angle = (degree * Math.PI) / 180 + ringAngle
                    const inner = R + 10
                    const outer = R + (degree % 30 === 0 ? 17 : 13)

                    context.moveTo(ex + Math.cos(angle) * inner, ey + Math.sin(angle) * inner)
                    context.lineTo(ex + Math.cos(angle) * outer, ey + Math.sin(angle) * outer)
                }

                context.stroke()

                context.strokeStyle = 'rgba(237, 230, 216, 0.16)'
                context.beginPath()
                context.moveTo(ex - R * 0.12, ey)
                context.lineTo(ex - R * 0.04, ey)
                context.moveTo(ex + R * 0.04, ey)
                context.lineTo(ex + R * 0.12, ey)
                context.moveTo(ex, ey - R * 0.12)
                context.lineTo(ex, ey - R * 0.04)
                context.moveTo(ex, ey + R * 0.04)
                context.lineTo(ex, ey + R * 0.12)
                context.stroke()

                // Shockwaves off the rim when something is logged.
                for (const burst of bursts) {
                    for (const delay of [0, 140]) {
                        const t = clamp01((now - burst.born - delay) / 950)

                        if (t <= 0 || t >= 1) continue

                        context.strokeStyle = `rgba(${BRASS}, ${0.6 * Math.pow(1 - t, 1.5)})`
                        context.lineWidth = 1.5
                        context.beginPath()
                        context.arc(burst.cx, burst.cy, R + 8 + easeOut(t) * 80, 0, Math.PI * 2)
                        context.stroke()
                    }
                }

                context.restore()
            }

            for (let i = bursts.length - 1; i >= 0; i--) {
                if (now - bursts[i].born > 1200) bursts.splice(i, 1)
            }

            if (next !== viewing.current) {
                viewing.current = next

                // Keep the last object's text while its label fades out.
                if (next) setCurrent(next)
            }

            if (next && inView && inView.distance < R * 0.35) {
                if (!dwell || dwell.id !== next.id) dwell = { id: next.id, since: now }
                else if (now - dwell.since > (still ? 350 : ACQUIRE_MS) && !foundRef.current.includes(next.id)) {
                    const updated = [...foundRef.current, next.id]

                    foundRef.current = updated
                    foundAt.set(next.id, now)

                    if (!still) bursts.push({
                        x: next.field.x * width,
                        y: next.field.y * height,
                        cx: ex,
                        cy: ey,
                        size: Math.min(objectSize(next) * 0.5, R * 0.6),
                        born: now
                    })
                    setFound(updated)
                    setAnnouncement(`Found ${next.name}. ${updated.length} of ${DEEP_SKY_OBJECTS.length}.`)

                    try {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
                    }
                    catch {
                        // Progress simply will not be remembered.
                    }
                }
            }
            else {
                dwell = null
            }

            if (labelRef.current) {
                const labelWidth = Math.min(300, width - 32)
                const right = ex + R + 32
                const left = ex - R - 32 - labelWidth
                let lx = right
                let ly = Math.max(16, Math.min(height - 180, ey - 40))

                if (right + labelWidth > width - 16) {
                    if (left >= 16) {
                        lx = left
                    }
                    else {
                        // Too narrow for either side (phones, tablets): sit above or below the eyepiece instead.
                        lx = Math.max(16, Math.min(width - labelWidth - 16, ex - labelWidth / 2))
                        ly = ey + R + 24 + 190 < height ? ey + R + 24 : Math.max(16, ey - R - 24 - 190)
                    }
                }

                labelRef.current.style.transform = `translate3d(${lx}px, ${ly}px, 0)`
                labelRef.current.style.opacity = next ? String(Math.min(1, alpha)) : '0'
            }

            if (visible && !disposed) frame = requestAnimationFrame(draw)
        }

        const start = () => {
            cancelAnimationFrame(frame)
            lastNow = 0
            frame = requestAnimationFrame(draw)
        }

        resize()

        const observer = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting

            if (visible) start()
        }, { rootMargin: '100px 0px' })

        observer.observe(field)

        const resizeObserver = new ResizeObserver(resize)

        resizeObserver.observe(field)

        const spriteSize = Math.min(512, Math.round(420 * Math.min(window.devicePixelRatio || 1, 2) * 0.6))

        Promise.all(DEEP_SKY_OBJECTS.map(object => buildSprite(object, spriteSize).catch(() => ({ object, sharp: null, soft: null }))))
            .then(result => {
                if (!disposed) sprites = result
            })
            .catch(() => undefined)

        const unregister = registerSkyPointer(field, () => {
            const object = viewing.current

            return object ? { ra: object.ra, dec: object.dec } : null
        })

        return () => {
            disposed = true
            cancelAnimationFrame(frame)
            observer.disconnect()
            resizeObserver.disconnect()
            unregister()
        }
    }, [])

    const updatePointer = (clientX: number, clientY: number) => {
        const field = fieldRef.current

        if (!field) return

        const rect = field.getBoundingClientRect()

        pointer.current = {
            x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
            y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
            active: true
        }
    }

    const handleKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? 0.06 : 0.02
        const moves: Record<string, [number, number]> = {
            ArrowLeft: [-step, 0],
            ArrowRight: [step, 0],
            ArrowUp: [0, -step * 1.6],
            ArrowDown: [0, step * 1.6]
        }
        const move = moves[event.key]

        if (!move) return

        event.preventDefault()
        pointer.current = {
            x: Math.min(1, Math.max(0, pointer.current.x + move[0])),
            y: Math.min(1, Math.max(0, pointer.current.y + move[1])),
            active: true
        }
    }

    return (
        <section id='eyepiece' aria-labelledby='eyepiece-title' className='relative py-24 md:py-32'>
            <div className='container-page grid gap-8 lg:grid-cols-12 lg:items-end'>
                <div className='lg:col-span-7'>
                    <p className='label-mono text-brass'>
                        The Eyepiece
                    </p>
                    <RevealLines
                        id='eyepiece-title'
                        lines={['The dark is not', 'as empty as it looks.']}
                        className='display-serif mt-5 text-5xl text-star md:text-7xl'
                    />
                </div>
                <Reveal variant='wipe' className='lg:col-span-5'>
                    <p className='text-star-2'>
                        {touch
                            ? 'Touch and drag across the dark below. Twelve objects are hiding in it, each one a real photograph from Hubble or the European Southern Observatory. Rest on one to log it, and watch your own constellation grow.'
                            : 'Move slowly across the dark below. Twelve objects are hiding in it, each one a real photograph from Hubble or the European Southern Observatory. Rest on one to log it, and watch your own constellation grow.'}
                    </p>
                </Reveal>
            </div>

            <div
                ref={fieldRef}
                data-sky
                tabIndex={0}
                role='application'
                aria-label='Eyepiece field. Move the pointer, drag, or use the arrow keys to search for hidden objects.'
                onKeyDown={handleKey}
                onFocus={() => {
                    pointer.current = { ...pointer.current, active: true }
                }}
                onBlur={() => {
                    pointer.current = { ...pointer.current, active: false }
                }}
                onPointerMove={event => updatePointer(event.clientX, event.clientY)}
                onPointerDown={event => updatePointer(event.clientX, event.clientY)}
                onPointerEnter={event => updatePointer(event.clientX, event.clientY)}
                onTouchMove={event => {
                    // Vertical drags scroll the page (touch-pan-y) and cancel the pointer; touch events keep coming, so the eyepiece still follows the finger.
                    const point = event.touches[0]

                    if (point) updatePointer(point.clientX, point.clientY)
                }}
                onPointerLeave={event => {
                    if (event.pointerType === 'mouse') pointer.current = { ...pointer.current, active: false }
                }}
                className='relative mt-14 h-[74svh] min-h-[30rem] w-full touch-pan-y select-none overflow-hidden bg-black outline-none focus-visible:ring-1 focus-visible:ring-brass/60'
            >
                <div className='media absolute inset-0'>
                    <canvas ref={canvasRef} aria-hidden className='block h-full w-full' />
                </div>

                <div
                    ref={labelRef}
                    aria-hidden
                    className='pointer-events-none absolute left-0 top-0 w-[300px] max-w-[calc(100%-2rem)] opacity-0 transition-opacity duration-500'
                >
                    {current && <ObjectLabel key={current.id} object={current} />}
                </div>

                <div className='pointer-events-none absolute inset-x-0 bottom-0'>
                    <div className='container-page flex flex-wrap items-end justify-between gap-4 pb-6'>
                        <div className='pointer-events-auto'>
                            <p className='label-mono text-star-2'>
                                <span key={found.length} className={cn('text-star', styles.tick)}>
                                    {found.length}
                                </span>
                                {` of ${DEEP_SKY_OBJECTS.length} objects found`}
                            </p>
                            <div className='mt-2 flex gap-1' aria-hidden>
                                {DEEP_SKY_OBJECTS.map(object => (
                                    <span
                                        key={object.id}
                                        className={cn('h-px w-4', styles.segment, found.includes(object.id) ? cn('bg-brass', styles.segmentFound) : 'bg-hair-2')}
                                    />
                                ))}
                            </div>
                            <FadeLink href='/catalogue' className='group mt-4 inline-flex items-center gap-3 text-star'>
                                <span className='h-px w-6 bg-brass transition-all duration-500 group-hover:w-10' />
                                <span className='label-mono'>
                                    See the full catalogue
                                </span>
                            </FadeLink>
                        </div>
                        <p className='label-mono text-star-3'>
                            {found.length === DEEP_SKY_OBJECTS.length ? 'All twelve found. You have a better eye than most of our visitors.' : touch ? 'Touch, drag and rest on an object' : 'Move the eyepiece and rest on an object, or use the arrow keys'}
                        </p>
                    </div>
                </div>

                <p className='sr-only' aria-live='polite'>
                    {announcement}
                </p>
            </div>
        </section>
    )
}
